import _ from 'lodash';
import moment from 'moment';

import { convertDate, stringToDate } from './validateDateUtil';
import { isNullOrUndefinedOrEmptyStr } from './stringUtil';
import { validExpirationDateReturnSimpleMessage } from './dateSelectorUtility';
import {
  DATE_TIME_FORMAT,
  NUMBER_YEAR_OF_DUPLICATE_CONSTRUCTION_INDUSTRY,
  PREFECTURE_LISTS,
} from './constants';
import { INDUSTRY } from './roanConstants';

const SEPARATOR = '　';

/**
 *
 * @param {object} license
 * @param {array} currentLicenses
 * @returns {boolean}
 */
export const isDuplicateWithCurrentLicenses = (license, currentLicenses) => {
  const licensesWithoutOtherIndustry = _.filter(
    currentLicenses,
    row => !_.get(row, 'other_authorized_industry_invalid_flag')
  );
  return _.some(licensesWithoutOtherIndustry, group =>
    isDuplicateLicenseInfo(license, group)
  );
};

/**
 * get licenses invalid
 * @param {array} companyBranchPermissions
 * @returns {array}
 */
export const getDataWithoutInvalidFlag = companyBranchPermissions => {
  return _.filter(
    companyBranchPermissions,
    row =>
      !_.get(row, 'invalid_flag') &&
      !_.get(row, 'other_authorized_industry_invalid_flag')
  );
};

/**
 * check two license with the same information
 * @param {object} license
 * @param {object} other
 * @param {boolean} isIgnoreId
 * @returns {boolean}
 */
export const isDuplicateLicenseInfo = (license, other, isIgnoreId = false) => {
  return (
    (isIgnoreId || _.get(license, 'id') !== _.get(other, 'id')) &&
    Number(license.permitted_source_section) ===
      Number(other.permitted_source_section) &&
    Number(license.construction_industry_permission_section) ===
      Number(other.construction_industry_permission_section) &&
    String(license.permission_number_l) === String(other.permission_number_l) &&
    String(license.permission_number_r) === String(other.permission_number_r) &&
    convertDate(license.license_update_date) ===
      convertDate(other.license_update_date)
  );
};

/**
 * get authorized industry codes
 * @param industries
 * @returns {array}
 */
const getAuthorizedIndustryCodes = industries => {
  const industryCodes = [];
  _.forEach(industries, industry => {
    if (
      !_.get(industry, '_destroy') &&
      _.get(industry, 'authorized_industry_codes')
    ) {
      industryCodes.push(industry.authorized_industry_codes);
    }
  });
  return industryCodes;
};

/**
 * is duplicate construction industry license
 * @param {object} data
 * @param {object} dataCompare
 * @returns {boolean}
 */
export const isDuplicateConstructionIndustry = (data, dataCompare) => {
  let isDuplicate = false;
  if (data.id === dataCompare.id) {
    return isDuplicate;
  }
  if (
    isDuplicateLicensePeriod(
      dataCompare.license_update_date,
      data.license_update_date
    )
  ) {
    const industryCodesDataCompare = getAuthorizedIndustryCodes(
      dataCompare.company_branch_permissions
    );
    const industryCodes = getAuthorizedIndustryCodes(
      data.company_branch_permissions
    );
    isDuplicate = _.some(industryCodesDataCompare, val =>
      _.includes(industryCodes, val)
    );
  }
  return isDuplicate;
};

const isDuplicateLicensePeriod = (licenseUpdateDate1, licenseUpdateDate2) => {
  let startDate = licenseUpdateDate2;
  let dateCompare = licenseUpdateDate1;
  if (!moment(startDate).isValid() || !moment(dateCompare).isValid()) {
    return false;
  }
  if (moment(licenseUpdateDate1).isBefore(licenseUpdateDate2)) {
    startDate = licenseUpdateDate1;
    dateCompare = licenseUpdateDate2;
  }
  const endDate = moment(startDate).add(
    NUMBER_YEAR_OF_DUPLICATE_CONSTRUCTION_INDUSTRY,
    'years'
  );
  const [year, month, day] = stringToDate(startDate);
  if (!(Number(month) === 2 && Number(day) === 29)) {
    endDate.subtract(1, 'days');
  }
  return moment(dateCompare).isBetween(startDate, endDate, 'day', []);
};

/**
 * get prefecture name by id
 * @param {number} id
 * @returns {string}
 */
export const getPrefectureName = id => {
  return `${_.get(PREFECTURE_LISTS, id) || ''}知事許可`;
};

/**
 * get permit type name
 * @param {string} type
 * @returns {string}
 */
export const getPermitTypeName = type => {
  if (type) {
    return _.get(INDUSTRY.permissions.construction, `${type}.name`);
  }
  return SEPARATOR;
};

/**
 * get bureaux name by id
 * @param {number} bureauxId
 * @param {array} bureaux
 * @returns {string}
 */
export const getBureauxName = (bureauxId, bureaux) => {
  if (bureauxId) {
    const data = _.find(bureaux, item => Number(item.id) === Number(bureauxId));
    if (data) {
      return _.get(data, 'bureau_short_name');
    }
  }
  return '';
};

/**
 * get permitted source section
 * @param {object} data
 * @returns {string}
 */
export const getPermittedSourceSection = data => {
  let permittedSourceSection = '';
  switch (Number(_.get(data, 'permitted_source_section'))) {
    case INDUSTRY.permissions.source[1].id:
      permittedSourceSection = '大臣許可';
      break;
    case INDUSTRY.permissions.source[2].id:
      permittedSourceSection = getPrefectureName(
        _.get(data, 'prefectures_code_id')
      );
      break;
    default:
      break;
  }
  return permittedSourceSection;
};

/**
 * get permission number
 * @param {object} data
 * @param {array} bureaux
 * @returns {string}
 */
export const getPermissionNumber = (data, bureaux) => {
  const permitTypeName = getPermitTypeName(
    Number(_.get(data, 'construction_industry_permission_section'))
  );
  const permissionNumberL = _.defaultTo(
    _.get(data, 'permission_number_l'),
    SEPARATOR
  );
  const bureauxName = getBureauxName(
    data.regional_development_bureau_id,
    bureaux
  );
  const permissionNumberR = _.defaultTo(
    _.get(data, 'permission_number_r'),
    SEPARATOR
  );
  return `（${permitTypeName}-${permissionNumberL}） ${bureauxName} 第${permissionNumberR}号`;
};

/**
 * get date string
 * @param {string} date
 * @returns {string}
 */
export const getDateString = date => {
  if (_.isUndefined(date)) {
    return '';
  }

  const createdAt = moment(date);
  if (!createdAt.isValid()) {
    return '';
  }
  return createdAt.format(DATE_TIME_FORMAT.slashDateTimeFormat);
};

/**
 * get document file name
 * @param {object} file
 * @param {array} documentCodes
 * @returns {string}
 */
export const getDocumentFileName = (file, documentCodes) => {
  const fileTypeS = _.find(
    documentCodes,
    item => item.file_typeS === file.file_typeS
  );
  const fileTypeSName = _.defaultTo(_.get(fileTypeS, 'file_typeS_name'), '');
  const fileName = _.get(file, 'file_name');
  const createdAt = getDateString(_.get(file, 'created_at'));
  if (createdAt) {
    return `${fileTypeSName}${SEPARATOR}${fileName}${SEPARATOR}(${createdAt})`;
  }
  return `${fileTypeSName}${SEPARATOR}${fileName}`;
};

/**
 * get license industry string
 * @param {object} data
 * @returns {string}
 */
export const getLicenseIndustryString = data => {
  return _.filter(data.company_branch_permissions, ({ _destroy }) => !_destroy)
    .map(industry => {
      return _.isEmpty(_.get(industry, 'authorized_industry_codes'))
        ? _.get(industry, 'authorized_industry_names')
        : _.get(industry, 'authorized_industry_short_name');
    })
    .join('、');
};

/**
 * get expire message
 * @param data
 * @returns {string|null}
 */
export const getExpireMessage = data => {
  const licenseLimitDate = _.get(data, 'license_limit_date');
  if (isNullOrUndefinedOrEmptyStr(licenseLimitDate)) {
    return null;
  }
  return validExpirationDateReturnSimpleMessage(licenseLimitDate, true);
};



// WEBPACK FOOTER //
// ./src/utilities/CompanySaftyLicenseUtil.js